import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  BarChart3, 
  TrendingUp, 
  DollarSign, 
  Calendar, 
  Package,
  Ship,
  Users,
  AlertTriangle,
  CheckCircle2,
  Download,
  Filter,
  FileText,
  FileSpreadsheet,
  Clock,
  Archive,
  Globe
} from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL_EXPORT as BACKEND_URL, API } from '../../config';

const ReportsManagement = ({ user }) => {
  const [vehicles, setVehicles] = useState([]);
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('all');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [vehiclesRes, ordersRes, usersRes] = await Promise.all([
        axios.get(`${API}/vehicles`),
        axios.get(`${API}/orders`),
        user?.role === 'admin' ? axios.get(`${API}/users`) : Promise.resolve({ data: [] })
      ]);

      setVehicles(vehiclesRes.data.vehicles || vehiclesRes.data);
      const ordersList = ordersRes.data.orders || ordersRes.data;
      setOrders(ordersList);
      setUsers(usersRes.data.users || usersRes.data);

      // Fetch payments for all orders concurrently (avoids long sequential waits)
      const paymentsPerOrder = await Promise.all(
        ordersList.map(async (order) => {
          try {
            const paymentRes = await axios.get(`${API}/payments/order/${order.id}`);
            return paymentRes.data.map(p => ({ ...p, order }));
          } catch (error) {
            console.error(`Error fetching payments for order ${order.id}:`, error);
            return [];
          }
        })
      );
      setPayments(paymentsPerOrder.flat());
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  // Analytics calculations
  const calculateProfitLoss = () => {
    // Only calculate profit for delivered vehicles
    return vehicles
      .filter(vehicle => {
        // Check if vehicle has a delivered order
        const order = orders.find(o => o.vehicle_id === vehicle.id);
        return order && order.status === 'delivered';
      })
      .map(vehicle => {
        const totalCosts = [
          vehicle.purchase_cost_jpy || 0,
          vehicle.transport_cost_jpy || 0,
          vehicle.freight_cost_jpy || 0,
          vehicle.inspection_cost_jpy || 0
        ].reduce((sum, cost) => sum + cost, 0);

        // Get revenue from the delivered order
        const order = orders.find(o => o.vehicle_id === vehicle.id);
        const revenue = order ? (order.total_amount_jpy || 0) : 0;
        const profit = revenue - totalCosts;
        const margin = revenue > 0 ? ((profit / revenue) * 100) : 0;

        return {
          ...vehicle,
          totalCosts,
          revenue,
          profit,
          margin,
          status: order.status
        };
      });
  };

  const calculatePaymentStatus = () => {
    const pendingBalances = [];
    
    orders.forEach(order => {
      const orderPayments = payments.filter(p => p.order_id === order.id);
      const totalPaid = orderPayments.reduce((sum, p) => sum + (p.amount_usd || 0), 0);
      const balance = order.total_amount_usd - totalPaid;
      
      if (balance > 0) {
        pendingBalances.push({
          orderId: order.id,
          customerId: order.customer_id,
          totalAmount: order.total_amount_usd,
          paidAmount: totalPaid,
          balance: balance,
          progress: Math.min((totalPaid / order.total_amount_usd) * 100, 100)
        });
      }
    });

    return pendingBalances;
  };

  const getShippingStatusSummary = () => {
    const upcoming = vehicles.filter(v => {
      if (!v.etd) return false;
      const etd = new Date(v.etd);
      const now = new Date();
      const diffTime = etd.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays <= 7 && diffDays >= 0;
    });

    const overdue = vehicles.filter(v => {
      if (!v.eta || v.status === 'delivered') return false;
      const eta = new Date(v.eta);
      const now = new Date();
      return eta < now;
    });

    return { upcoming, overdue };
  };

  const getCustomerWiseStats = () => {
    const customerStats = {};
    
    orders.forEach(order => {
      const customerId = order.customer_id;
      if (!customerStats[customerId]) {
        customerStats[customerId] = {
          customerId,
          orderCount: 0,
          totalValue: 0,
          paidAmount: 0,
          pendingAmount: 0
        };
      }
      
      customerStats[customerId].orderCount += 1;
      customerStats[customerId].totalValue += order.total_amount_usd;
      
      const orderPayments = payments.filter(p => p.order_id === order.id);
      const paid = orderPayments.reduce((sum, p) => sum + (p.amount_usd || 0), 0);
      customerStats[customerId].paidAmount += paid;
      customerStats[customerId].pendingAmount += (order.total_amount_usd - paid);
    });
    
    return Object.values(customerStats);
  };

  // Time-Based Analytics
  const getMonthlyRevenue = () => {
    const monthlyData = {};
    
    orders.forEach(order => {
      if (order.order_date) {
        const date = new Date(order.order_date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = {
            month: monthKey,
            revenue: 0,
            orders: 0,
            profit: 0
          };
        }
        
        monthlyData[monthKey].revenue += order.total_amount_usd || 0;
        monthlyData[monthKey].orders += 1;
        
        // Calculate profit for this order
        const vehicle = vehicles.find(v => v.id === order.vehicle_id);
        if (vehicle) {
          const costs = [
            vehicle.purchase_cost_jpy || 0,
            vehicle.transport_cost_jpy || 0,
            vehicle.freight_cost_jpy || 0,
            vehicle.inspection_cost_jpy || 0
          ].reduce((sum, cost) => sum + cost, 0);
          
          const exchangeRate = vehicle.exchange_rate || 145;
          const costUSD = costs / exchangeRate;
          monthlyData[monthKey].profit += (order.total_amount_usd - costUSD);
        }
      }
    });
    
    return Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));
  };

  // Inventory Analytics
  const getInventoryAging = () => {
    const now = new Date();
    
    return vehicles.map(vehicle => {
      const createdDate = new Date(vehicle.created_at);
      const daysInInventory = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
      
      let ageCategory = 'Fresh'; // 0-30 days
      if (daysInInventory > 90) ageCategory = 'Aging'; // 90+ days
      else if (daysInInventory > 60) ageCategory = 'Mature'; // 60-90 days
      else if (daysInInventory > 30) ageCategory = 'Standard'; // 30-60 days
      
      return {
        ...vehicle,
        daysInInventory,
        ageCategory
      };
    }).filter(v => v.status !== 'delivered'); // Only show non-delivered vehicles
  };

  const getStockByBrand = () => {
    const brandStats = {};
    
    vehicles.forEach(vehicle => {
      if (vehicle.status !== 'delivered') {
        const brand = vehicle.brand || 'Unknown';
        if (!brandStats[brand]) {
          brandStats[brand] = {
            brand,
            count: 0,
            totalValue: 0
          };
        }
        brandStats[brand].count += 1;
        brandStats[brand].totalValue += (vehicle.confirmed_sale_usd || vehicle.estimated_sale_usd || 0);
      }
    });
    
    return Object.values(brandStats).sort((a, b) => b.count - a.count);
  };

  // Customer Analytics
  const getCustomers = () => {
    return users.filter(u => u.role === 'customer' || u.source === 'Website');
  };

  const getCustomerGrowth = () => {
    const monthlyData = {};

    getCustomers().forEach(customer => {
      if (customer.created_at) {
        const date = new Date(customer.created_at);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = {
            month: monthKey,
            website: 0,
            crm: 0
          };
        }

        if (customer.source === 'Website') {
          monthlyData[monthKey].website += 1;
        } else {
          monthlyData[monthKey].crm += 1;
        }
      }
    });

    return Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));
  };

  const getCustomersByCountry = () => {
    const countryStats = {};

    getCustomers().forEach(customer => {
      const country = customer.country || 'Unknown';
      if (!countryStats[country]) {
        countryStats[country] = { country, count: 0 };
      }
      countryStats[country].count += 1;
    });

    return Object.values(countryStats).sort((a, b) => b.count - a.count);
  };

  // Export Functions
  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => {
        const value = row[header];
        return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Report exported successfully!');
  };

  const exportCurrentReport = () => {
    // This will be called from individual tabs with specific data
    toast.info('Select a report tab to export');
  };

  const profitLossData = calculateProfitLoss();
  const pendingBalances = calculatePaymentStatus();
  const shippingStatus = getShippingStatusSummary();
  const customerStats = getCustomerWiseStats();
  const monthlyRevenue = getMonthlyRevenue();
  const inventoryAging = getInventoryAging();
  const stockByBrand = getStockByBrand();
  const customers = getCustomers();
  const customerGrowth = getCustomerGrowth();
  const customersByCountry = getCustomersByCountry();

  // Summary stats
  const totalRevenue = profitLossData.reduce((sum, item) => sum + item.revenue, 0);
  const totalCosts = profitLossData.reduce((sum, item) => sum + item.totalCosts, 0);
  const totalProfit = totalRevenue - totalCosts;
  const averageMargin = profitLossData.length > 0 
    ? profitLossData.reduce((sum, item) => sum + item.margin, 0) / profitLossData.length 
    : 0;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-64 mb-4"></div>
          <div className="h-4 bg-slate-200 rounded w-96"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="reports-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reports & Analytics</h1>
          <p className="text-slate-600">Business insights, profit analysis, and performance tracking</p>
        </div>
        <div className="flex items-center space-x-3">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-40">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="30">Last 30 Days</SelectItem>
              <SelectItem value="90">Last 90 Days</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Revenue</p>
                <p className="text-2xl font-bold text-green-600">¥{totalRevenue.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Costs</p>
                <p className="text-2xl font-bold text-orange-600">¥{totalCosts.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <Package className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Net Profit</p>
                <p className={`text-2xl font-bold ${totalProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  ¥{totalProfit.toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Avg. Margin</p>
                <p className="text-2xl font-bold text-blue-600">{averageMargin.toFixed(1)}%</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="profit-loss" className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="profit-loss">Profit & Loss</TabsTrigger>
          <TabsTrigger value="time-based">Time Analytics</TabsTrigger>
          <TabsTrigger value="inventory">Inventory</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
          <TabsTrigger value="shipping">Shipping</TabsTrigger>
          <TabsTrigger value="customers">Customers</TabsTrigger>
        </TabsList>

        <TabsContent value="profit-loss" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Profit & Loss Analysis</CardTitle>
                  <CardDescription>Detailed profitability breakdown per vehicle</CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => exportToCSV(profitLossData.map(item => ({
                    'Stock No': item.stock_no,
                    'Vehicle': `${item.year} ${item.model}`,
                    'Revenue (JPY)': item.revenue,
                    'Total Costs (JPY)': item.totalCosts,
                    'Profit (JPY)': item.profit,
                    'Margin %': item.margin.toFixed(2),
                    'Status': item.status
                  })), 'profit_loss_report')}
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Vehicle</TableHead>
                      <TableHead>Revenue</TableHead>
                      <TableHead>Total Costs</TableHead>
                      <TableHead>Profit/Loss</TableHead>
                      <TableHead>Margin %</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {profitLossData.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{item.year} {item.model}</p>
                            <p className="text-sm text-slate-500">{item.stock_no}</p>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          ¥{item.revenue.toLocaleString()}
                        </TableCell>
                        <TableCell>
                          ¥{item.totalCosts.toLocaleString()}
                        </TableCell>
                        <TableCell className={item.profit >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                          ¥{item.profit.toLocaleString()}
                        </TableCell>
                        <TableCell className={item.margin >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {item.margin.toFixed(1)}%
                        </TableCell>
                        <TableCell>
                          <Badge className={`
                            ${item.status === 'delivered' ? 'bg-green-100 text-green-800' : 
                              item.status === 'shipped' ? 'bg-blue-100 text-blue-800' : 
                              'bg-yellow-100 text-yellow-800'}
                          `}>
                            {item.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="time-based" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Monthly Revenue Trends</CardTitle>
                  <CardDescription>Revenue, orders, and profit by month</CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => exportToCSV(monthlyRevenue.map(item => ({
                    'Month': item.month,
                    'Revenue (USD)': item.revenue.toFixed(2),
                    'Orders': item.orders,
                    'Profit (USD)': item.profit.toFixed(2),
                    'Avg Order Value': (item.revenue / item.orders).toFixed(2)
                  })), 'monthly_revenue_report')}
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {monthlyRevenue.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead className="text-right">Orders</TableHead>
                        <TableHead className="text-right">Revenue</TableHead>
                        <TableHead className="text-right">Profit</TableHead>
                        <TableHead className="text-right">Avg Order Value</TableHead>
                        <TableHead className="text-right">Profit Margin</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {monthlyRevenue.map((item, index) => {
                        const avgOrderValue = item.orders > 0 ? item.revenue / item.orders : 0;
                        const profitMargin = item.revenue > 0 ? (item.profit / item.revenue) * 100 : 0;
                        const prevMonth = index > 0 ? monthlyRevenue[index - 1] : null;
                        const revenueChange = prevMonth ? ((item.revenue - prevMonth.revenue) / prevMonth.revenue) * 100 : 0;
                        
                        return (
                          <TableRow key={item.month}>
                            <TableCell className="font-medium">
                              {new Date(item.month + '-01').toLocaleDateString('en-US', { 
                                year: 'numeric', 
                                month: 'short' 
                              })}
                            </TableCell>
                            <TableCell className="text-right">{item.orders}</TableCell>
                            <TableCell className="text-right font-medium">
                              <div>${item.revenue.toLocaleString()}</div>
                              {prevMonth && (
                                <div className={`text-xs ${revenueChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {revenueChange >= 0 ? '↑' : '↓'} {Math.abs(revenueChange).toFixed(1)}%
                                </div>
                              )}
                            </TableCell>
                            <TableCell className={`text-right font-medium ${item.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              ${item.profit.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right">
                              ${avgOrderValue.toLocaleString()}
                            </TableCell>
                            <TableCell className={`text-right ${profitMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {profitMargin.toFixed(1)}%
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No Data Available</h3>
                  <p className="text-slate-500">No orders found for time-based analysis.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="inventory" className="space-y-4">
          {/* Inventory Aging Report */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Vehicle Aging Report</CardTitle>
                  <CardDescription>How long vehicles have been in inventory</CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => exportToCSV(inventoryAging.map(item => ({
                    'Stock No': item.stock_no,
                    'Vehicle': `${item.year} ${item.brand} ${item.model}`,
                    'Days in Inventory': item.daysInInventory,
                    'Age Category': item.ageCategory,
                    'Status': item.status
                  })), 'inventory_aging_report')}
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {inventoryAging.length > 0 ? (
                <div className="space-y-4">
                  {/* Age Summary Cards */}
                  <div className="grid grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-slate-600">Fresh (0-30d)</div>
                        <div className="text-2xl font-bold text-green-600">
                          {inventoryAging.filter(v => v.ageCategory === 'Fresh').length}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-slate-600">Standard (31-60d)</div>
                        <div className="text-2xl font-bold text-blue-600">
                          {inventoryAging.filter(v => v.ageCategory === 'Standard').length}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-slate-600">Mature (61-90d)</div>
                        <div className="text-2xl font-bold text-yellow-600">
                          {inventoryAging.filter(v => v.ageCategory === 'Mature').length}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-slate-600">Aging (90+d)</div>
                        <div className="text-2xl font-bold text-red-600">
                          {inventoryAging.filter(v => v.ageCategory === 'Aging').length}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Detailed Table */}
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Stock No</TableHead>
                          <TableHead>Vehicle</TableHead>
                          <TableHead>Days in Inventory</TableHead>
                          <TableHead>Age Category</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {inventoryAging.sort((a, b) => b.daysInInventory - a.daysInInventory).map((vehicle) => (
                          <TableRow key={vehicle.id}>
                            <TableCell className="font-medium">{vehicle.stock_no}</TableCell>
                            <TableCell>
                              {vehicle.year} {vehicle.brand} {vehicle.model}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-slate-400" />
                                {vehicle.daysInInventory} days
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className={`
                                ${vehicle.ageCategory === 'Fresh' ? 'bg-green-100 text-green-800' : 
                                  vehicle.ageCategory === 'Standard' ? 'bg-blue-100 text-blue-800' : 
                                  vehicle.ageCategory === 'Mature' ? 'bg-yellow-100 text-yellow-800' : 
                                  'bg-red-100 text-red-800'}
                              `}>
                                {vehicle.ageCategory}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{vehicle.status}</Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Archive className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No Active Inventory</h3>
                  <p className="text-slate-500">All vehicles have been delivered.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Stock by Brand */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Stock by Brand</CardTitle>
                  <CardDescription>Current inventory breakdown by manufacturer</CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => exportToCSV(stockByBrand.map(item => ({
                    'Brand': item.brand,
                    'Units in Stock': item.count,
                    'Total Value (USD)': item.totalValue.toFixed(2)
                  })), 'stock_by_brand_report')}
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {stockByBrand.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Brand</TableHead>
                        <TableHead className="text-right">Units in Stock</TableHead>
                        <TableHead className="text-right">Total Value</TableHead>
                        <TableHead className="text-right">% of Inventory</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {stockByBrand.map((item) => {
                        const totalUnits = stockByBrand.reduce((sum, b) => sum + b.count, 0);
                        const percentage = (item.count / totalUnits) * 100;
                        
                        return (
                          <TableRow key={item.brand}>
                            <TableCell className="font-medium">{item.brand}</TableCell>
                            <TableCell className="text-right">{item.count}</TableCell>
                            <TableCell className="text-right">${item.totalValue.toLocaleString()}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-20 bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-blue-600 h-2 rounded-full" 
                                    style={{ width: `${percentage}%` }}
                                  ></div>
                                </div>
                                <span className="text-sm">{percentage.toFixed(1)}%</span>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No stock data available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Pending Balance Report</CardTitle>
                  <CardDescription>Orders with outstanding payment balances</CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => exportToCSV(pendingBalances.map(item => ({
                    'Order ID': item.orderId.slice(-8).toUpperCase(),
                    'Total Amount (USD)': item.totalAmount,
                    'Paid Amount (USD)': item.paidAmount,
                    'Balance (USD)': item.balance,
                    'Progress %': item.progress.toFixed(2)
                  })), 'pending_balances_report')}
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {pendingBalances.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Order ID</TableHead>
                        <TableHead>Total Amount</TableHead>
                        <TableHead>Paid Amount</TableHead>
                        <TableHead>Balance</TableHead>
                        <TableHead>Progress</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingBalances.map((item) => (
                        <TableRow key={item.orderId}>
                          <TableCell className="font-mono">
                            #{item.orderId.slice(-8).toUpperCase()}
                          </TableCell>
                          <TableCell>${item.totalAmount.toLocaleString()}</TableCell>
                          <TableCell className="text-green-600">
                            ${item.paidAmount.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-orange-600 font-medium">
                            ${item.balance.toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <div className="w-20 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-green-600 h-2 rounded-full" 
                                  style={{ width: `${item.progress}%` }}
                                ></div>
                              </div>
                              <span className="text-sm text-slate-600">{item.progress.toFixed(0)}%</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">All Payments Current</h3>
                  <p className="text-slate-500">No outstanding balances found.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="shipping" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Ship className="h-5 w-5" />
                  <span>Upcoming ETD</span>
                </CardTitle>
                <CardDescription>Vehicles departing within 7 days</CardDescription>
              </CardHeader>
              <CardContent>
                {shippingStatus.upcoming.length > 0 ? (
                  <div className="space-y-3">
                    {shippingStatus.upcoming.map((vehicle) => (
                      <div key={vehicle.id} className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                        <div>
                          <p className="font-medium">{vehicle.year} {vehicle.model}</p>
                          <p className="text-sm text-slate-600">{vehicle.stock_no}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-blue-600">
                            {vehicle.etd ? new Date(vehicle.etd).toLocaleDateString() : 'TBD'}
                          </p>
                          <p className="text-xs text-slate-500">{vehicle.pol || 'Port TBD'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Ship className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500">No upcoming departures</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  <span>Overdue Arrivals</span>
                </CardTitle>
                <CardDescription>Vehicles past their ETA</CardDescription>
              </CardHeader>
              <CardContent>
                {shippingStatus.overdue.length > 0 ? (
                  <div className="space-y-3">
                    {shippingStatus.overdue.map((vehicle) => (
                      <div key={vehicle.id} className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                        <div>
                          <p className="font-medium">{vehicle.year} {vehicle.model}</p>
                          <p className="text-sm text-slate-600">{vehicle.stock_no}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-red-600">
                            {vehicle.eta ? new Date(vehicle.eta).toLocaleDateString() : 'No ETA'}
                          </p>
                          <p className="text-xs text-slate-500">{vehicle.pod || 'Port TBD'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
                    <p className="text-slate-500">All shipments on time</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="customers" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Customer Analytics</h3>
              <p className="text-sm text-slate-600">Customer growth, sources, and geographic distribution</p>
            </div>
            <Button variant="outline" onClick={() => exportToCSV(customers.map(c => ({
              'Name': c.full_name,
              'Email': c.email,
              'Source': c.source || 'CRM',
              'Country': c.country || 'Unknown',
              'Currency': c.currency || 'USD',
              'Status': c.is_active ? 'Active' : 'Inactive',
              'Created': c.created_at ? new Date(c.created_at).toLocaleDateString() : 'N/A'
            })), 'customers_report')}>
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Total Customers</p>
                    <p className="text-2xl font-bold text-slate-900">{customers.length}</p>
                  </div>
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <Users className="h-6 w-6 text-indigo-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Website Signups</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {customers.filter(c => c.source === 'Website').length}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Globe className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">CRM Customers</p>
                    <p className="text-2xl font-bold text-emerald-600">
                      {customers.filter(c => c.source !== 'Website').length}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">New This Month</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {customers.filter(c => {
                        if (!c.created_at) return false;
                        const d = new Date(c.created_at);
                        const now = new Date();
                        return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
                      }).length}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Users className="h-6 w-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>New Customers by Month</CardTitle>
              <CardDescription>Website vs CRM signups over time</CardDescription>
            </CardHeader>
            <CardContent>
              {customerGrowth.length > 0 ? (
                <div className="space-y-4">
                  <div className="flex items-center space-x-6 text-sm">
                    <div className="flex items-center space-x-2">
                      <span className="w-3 h-3 rounded-sm bg-blue-500"></span>
                      <span className="text-slate-600">Website</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="w-3 h-3 rounded-sm bg-emerald-500"></span>
                      <span className="text-slate-600">CRM</span>
                    </div>
                  </div>
                  <div className="flex items-end h-48 gap-2">
                    {customerGrowth.map((item) => {
                      const total = item.website + item.crm;
                      const maxTotal = Math.max(...customerGrowth.map(g => g.website + g.crm), 1);
                      const height = (total / maxTotal) * 100;
                      return (
                        <div key={item.month} className="flex-1 flex flex-col items-center justify-end h-full">
                          <span className="text-xs text-slate-600 font-medium mb-1">{total}</span>
                          <div className="w-full flex items-end justify-center gap-0.5" style={{ height: `${height}%` }}>
                            <div
                              className="w-3 rounded-t bg-blue-500"
                              style={{ height: item.website > 0 ? `${(item.website / Math.max(total, 1)) * 100}%` : '0%' }}
                              title={`Website: ${item.website}`}
                            ></div>
                            <div
                              className="w-3 rounded-t bg-emerald-500"
                              style={{ height: item.crm > 0 ? `${(item.crm / Math.max(total, 1)) * 100}%` : '0%' }}
                              title={`CRM: ${item.crm}`}
                            ></div>
                          </div>
                          <span className="text-xs text-slate-500 mt-1">{item.month}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No customer growth data available</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Customers by Country</CardTitle>
              <CardDescription>Geographic distribution of customers</CardDescription>
            </CardHeader>
            <CardContent>
              {customersByCountry.length > 0 ? (
                <div className="space-y-3">
                  {customersByCountry.map((item) => {
                    const maxCount = customersByCountry[0].count;
                    return (
                      <div key={item.country} className="flex items-center space-x-3">
                        <span className="w-40 text-sm font-medium text-slate-700 truncate">{item.country}</span>
                        <div className="flex-1 bg-gray-100 rounded-full h-4">
                          <div
                            className="bg-indigo-500 h-4 rounded-full transition-all"
                            style={{ width: `${(item.count / maxCount) * 100}%` }}
                          ></div>
                        </div>
                        <span className="w-8 text-sm font-semibold text-slate-700">{item.count}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Globe className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No country data available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsManagement;